import React, { useContext, useEffect, useState } from 'react';
import * as styles from './bankDetails.styles';
import icon from '../../../assets/bankDetailsIcon.png';
import CustomInput from '../../../components/CustomInputs/CustomInput';
import CustomSelectInput from '../../../components/CustomSelectInput/CustomSelectInput';
import CustomButton from '../../../components/CustomButton/CustomButton';
import { FaArrowRight } from 'react-icons/fa';
import client from '../../../api/client';
import { UserContext } from '../../../contexts/UserContext';
import showToast from '../../../components/CustomToast/toastHelper';
import { Spinner } from 'flowbite-react';
import { useNavigate } from 'react-router-dom';

interface initTypes {
    accountNumber: string;
    accountName: string;
    bankId: number;
    subsidiaryId: number;
    accountType: string;
}

const BankDetails = () => {
    const { userDetails } = useContext(UserContext) ?? {};
    let { activeSubsidiary } = userDetails ?? {};
    const [banks, setBanks] = useState<any[]>([]);
    const [bankData, setBankData] = useState<any>();
    const [formData, setFormData] = useState({
        accountNumber: '',
        accountName: '',
        bankId: '',
        subsidiaryId: '',
        accountType: '',
        bankName: '',
        bankCode: ""
    });

    const navigate = useNavigate()
    const [selectedBank, setSelectedBank] = useState<{ bankId: number, bankCode: string }>({ bankId: 0, bankCode: '' });

    const [account, setAccount] = useState<any>();
    const [resolved, setResolved] = useState<boolean>(false);
    const [isResolving, setIsResolving] = useState<boolean>(false);
    const [action, setAction] = useState<string>('resolve')
    const [loading, setLoading] = useState<boolean>(false)


    useEffect(() => {
        const fetchData = async () => {
            await fetchBanks();
            await fetchCurrentBankDetails();
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (bankData) {
            setFormData({
                ...formData,
                accountName: bankData.accountName,
                bankName: bankData.bankName,
                accountType: "Savings",
                accountNumber: bankData.accountNumber,
                bankId: bankData.bankId
            });
        }
    }, [bankData]);

    const fetchBanks = async () => {
        try {
            const response = await client.get("/subsidiary/dashboard/banks");
            if (response.data.status === "success") {
                setBanks(response.data.banks);
            } else {
                console.log(response.data.message);
            }
        } catch (err) {
            console.error('error getting banks', err);
        }
    };

    useEffect(() => {
        if (formData.accountNumber.length === 10 && formData.bankCode) {
            if (formData.accountNumber !== bankData?.accountNumber || formData.bankCode !== bankData?.bankCode) {
                setAction('resolve');
                resolveAccount();
            }
        } else {
            setResolved(false);
            setIsResolving(false);
        }
    }, [formData.accountNumber, formData.bankCode]);

    const resolveAccount = async () => {
        setIsResolving(true);
        const payload = {
            accountNumber: formData.accountNumber,
            bankCode: formData.bankCode
        };
        try {
            const response = await client.post('/subsidiary/payout/account/resolve', payload);
            setAccount(response.data.data);
            setResolved(true);
            setIsResolving(false);
        } catch (err: any) {
            if (err?.response?.data?.message === 'Please login again') {
                sessionStorage.clear();
                window.location.href = '/';
            } else {
                const message = err?.response?.data?.message;
                console.error('error getting data', err);
                showToast(message, "error");
            }
        } finally {
            setIsResolving(false);
        }
    };

    const fetchCurrentBankDetails = async () => {
        try {
            const apiUrl = '/subsidiary/dashboard/onboarding/bank/view';
            const response = await client.get(`${apiUrl}`);
            if (response.data.status === "success" && response.data.data.length > 0) {
                const latestBankDetails = response.data.data[0];
                setBankData(latestBankDetails);
                setFormData({
                    ...formData,
                    accountName: latestBankDetails.accountName,
                    bankName: latestBankDetails.bankName,
                    accountType: "Savings",
                    accountNumber: latestBankDetails.accountNumber,
                    bankId: latestBankDetails.bankId,
                    bankCode: latestBankDetails.bankCode
                });
                setResolved(true);
            }
        } catch (err: any) {
            if (err?.response?.data?.message === 'Please login again') {
                sessionStorage.clear();
                window.location.href = '/';
            } else {
                const message = err?.response?.data?.message;
                console.error('error getting data', err);
                showToast(message, "error");
            }
        }
    };

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleBankChange = (selectedBankCode: string) => {
        const bankDetails = banks.find(bank => bank.bankCode === selectedBankCode);
        if (bankDetails) {
            setFormData({
                ...formData,
                bankId: bankDetails.id,
                bankCode: bankDetails.bankCode,
                bankName: bankDetails.name
            });
        }
    };

    const handleSubmit = async () => {
        setLoading(true)

        let payload: initTypes = {
            accountName: account ? account.accountName : formData.accountName,
            accountNumber: formData.accountNumber,
            bankId: Number(formData.bankId),
            subsidiaryId: activeSubsidiary?.id,
            accountType: formData.accountType
        };
        try {
            const response = await client.post("/subsidiary/dashboard/onboarding/bank/details", payload);
            showToast(response.data.message, "success");
            navigate('/business/onboarding/kyc/terms');
            window.location.reload()
            localStorage.setItem('activeCard', "Terms")

        } catch (err: any) {
            showToast(err.response.data.message, "error");
        }
        finally {
            setLoading(false)
        }
    };

    const accountName = account?.accountName || formData.accountName;

    return (
        <div className={styles.container}>
            <div className={styles.iconContainer}>
                <img src={icon} alt="Bank Details Icon" />
            </div>
            <div>
                <p className={styles.heading}>Bank Details</p>
                <p className={styles.subHeading}>
                    Kindly provide us with bank details to get your dashboard ready.
                    This is the primary bank account we send your settlements to.
                </p>

                <div className='my-4'>
                    {isResolving ? (
                        <Spinner />
                    ) : null}
                </div>
                <div className={styles.inputContainer}>
                    <CustomSelectInput
                        label='Bank Name'
                        items={banks}
                        value={formData.bankCode}
                        displayKey='name'
                        valueKey='bankCode'
                        className={styles.inputLabel}
                        onChange={handleBankChange}
                    />
                </div>
                <div className={styles.inputContainer}>
                    <CustomSelectInput
                        label='Account Type'
                        items={["Savings", "Current"]}
                        value={formData.accountType}
                        className={styles.inputLabel}
                        onChange={(value) => setFormData({ ...formData, accountType: value })}
                    />
                </div>
                <div className={`${styles.inputContainer}`}>
                    <CustomInput
                        label='Account Number'
                        name='accountNumber'
                        value={formData.accountNumber}
                        onChange={handleInputChange}
                    />
                </div>
                {accountName && (
                    <div className={styles.inputContainer}>
                        {accountName}
                    </div>
                )}
                <div className={styles.inputContainer}>
                    <CustomButton
                        text={loading ? <Spinner size='sm' /> : 'Save details'}
                        onClick={() => handleSubmit()}
                        icon={<FaArrowRight />}
                        className={styles.button}
                    />
                </div>
            </div>
        </div>
    );
};

export default BankDetails;
